import { useSyncExternalStore } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext.jsx";
import { isHandoff, subscribeHandoff } from "./lib/handoff.js";
import HandoffLock from "./components/HandoffLock.jsx";
import Login from "./pages/Login.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import AddEntry from "./pages/AddEntry.jsx";
import EditEntry from "./pages/EditEntry.jsx";
import Ledger from "./pages/Ledger.jsx";
import Mileage from "./pages/Mileage.jsx";
import More from "./pages/More.jsx";
import Receipts from "./pages/Receipts.jsx";
import ReviewReceipt from "./pages/ReviewReceipt.jsx";
import Crm from "./pages/Crm.jsx";
import CrmContact from "./pages/CrmContact.jsx";
import NewContact from "./pages/NewContact.jsx";
import MyCard from "./pages/MyCard.jsx";
import Finances from "./pages/Finances.jsx";
import Social, { SocialPost } from "./pages/Social.jsx";
import Money from "./pages/Money.jsx";
import Tithe from "./pages/Tithe.jsx";
import Deductions from "./pages/Deductions.jsx";
import Export from "./pages/Export.jsx";
import DocumentView from "./pages/DocumentView.jsx";
import Invoice from "./pages/Invoice.jsx";
import InvoicePrint from "./pages/InvoicePrint.jsx";
import AuditLog from "./pages/AuditLog.jsx";
import Duplicates from "./pages/Duplicates.jsx";
import Reconcile from "./pages/Reconcile.jsx";

function FullScreenLoader() {
  return (
    <div className="flex h-full items-center justify-center text-slate-400">
      <span className="animate-pulse">Loading…</span>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Hand-off mode replaces the router entirely — while the phone is in someone
// else's hands the app content doesn't exist, not even behind an overlay
// (nothing for the back button, a reload, or an accessibility cursor to
// reach). Only the PIN / Face ID unlock brings the app back.
function HandoffGate({ children }) {
  const locked = useSyncExternalStore(subscribeHandoff, isHandoff);
  return locked ? <HandoffLock /> : children;
}

export default function App() {
  return (
    <AuthProvider>
      <HandoffGate>
        <Routes>
        <Route path="/login" element={<Login />} />
        {[
          ["/", <Dashboard />],
          ["/add", <AddEntry />],
          ["/edit/:id", <EditEntry />],
          ["/ledger", <Ledger />],
          ["/receipts", <Receipts />],
          ["/receipts/:id/review", <ReviewReceipt />],
          ["/mileage", <Mileage />],
          ["/crm", <Crm />],
          ["/crm/new", <NewContact />],
          ["/mycard", <MyCard />],
          ["/dashboard", <Finances />],
          ["/social", <Social />],
          ["/social/:id", <SocialPost />],
          ["/money", <Money />],
          ["/tithe", <Tithe />],
          ["/deductions", <Deductions />],
          ["/export", <Export />],
          ["/documents/:id", <DocumentView />],
          ["/invoices/:id", <Invoice />],
          ["/invoices/:id/print", <InvoicePrint />],
          ["/audit", <AuditLog />],
          ["/duplicates", <Duplicates />],
          ["/reconcile", <Reconcile />],
          ["/crm/:id", <CrmContact />],
          ["/more", <More />],
        ].map(([path, el]) => (
          <Route key={path} path={path} element={<Protected>{el}</Protected>} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </HandoffGate>
    </AuthProvider>
  );
}
